// The minimum-version gate (PMU-114): the server names the oldest version of each app still
// allowed (GET /api/v1/app-config); an older app stops with "Update Vavr" instead of talking to
// an API it may no longer understand. Checked at start and whenever the app comes back to the
// foreground; a failed check never blocks (the app may be offline).
import { noteApp } from "./errorlog.ts";
import { useEffect, useState } from "react";
import { Button, Icon } from "@vavr/ui";

/** True when version `a` is older than `b` ("0.3.9" < "0.4.0"); anything unparsable is never older. */
export function olderThan(a: string, b: string): boolean {
  const pa = a.split(".").map(Number), pb = b.split(".").map(Number);
  if ([...pa, ...pb].some((n) => !Number.isInteger(n))) return false;
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const x = pa[i] ?? 0, y = pb[i] ?? 0;
    if (x !== y) return x < y;
  }
  return false;
}

export interface UpdateGate { required: boolean; minVersion: string | null; updateUrl: string | null }

/** Asks the server whether this build is still allowed. `appVersion` "web" (the website) is never gated. */
export function useUpdateGate(o: { apiBase: string; app: "guardian" | "teacher" | "kiosk"; appVersion: string }): UpdateGate {
  const [gate, setGate] = useState<UpdateGate>({ required: false, minVersion: null, updateUrl: null });
  useEffect(() => {
    noteApp(o.app, o.appVersion);
    if (o.appVersion === "web" || typeof fetch === "undefined") return;
    let live = true;
    const check = () => fetch(`${o.apiBase}/api/v1/app-config?app=${o.app}`).then((r) => (r.ok ? r.json() : null)).then((c: { min_version: string | null; update_url: string | null } | null) => {
      if (live && c) setGate({ required: !!c.min_version && olderThan(o.appVersion, c.min_version), minVersion: c.min_version, updateUrl: c.update_url });
    }, () => undefined);
    const wake = () => { if (document.visibilityState === "visible") void check(); };
    void check();
    document.addEventListener("visibilitychange", wake);
    return () => { live = false; document.removeEventListener("visibilitychange", wake); };
  }, [o.apiBase, o.app, o.appVersion]);
  return gate;
}

/** The whole-screen stop for a build that is too old. The door tablet has no store: the office updates it. */
export function UpdateRequired({ gate, appVersion, kiosk = false }: { gate: UpdateGate; appVersion: string; kiosk?: boolean }) {
  return (
    <div className="pmu-center pmu-update" role="alert">
      <div className="pmu-stack pmu-update__card">
        <Icon name="warning" />
        <h1 className="pmu-update__title">{kiosk ? "This tablet needs an update" : "Update Vavr to continue"}</h1>
        <p className="pmu-muted">
          {kiosk
            ? `Ask the office to install the latest Vavr Door on this tablet. This is version ${appVersion}; ${gate.minVersion} or newer is needed.`
            : `This version (${appVersion}) is no longer supported. Get the latest version from Google Play; it takes a minute.`}
        </p>
        {!kiosk && gate.updateUrl && <Button onClick={() => { location.href = gate.updateUrl!; }}>Update on Google Play</Button>}
      </div>
    </div>
  );
}
